import React, { useLayoutEffect, useState } from 'react';
import ProfaseModal from '../../commons/ProfaseModal';
import PageForm, { FormField } from '../../commons/PageForm';
import { useAppDispatch, useAppSelector } from '../../store';
import { createEntityForProveedor } from '../categoria-servicio.reducer';

const ACCENT = '#3b82f6';

export interface ServicioFormValues {
  descripcion: string;
  alias: string;
  precio: string;
}

const fields: FormField<ServicioFormValues>[] = [
  { name: 'descripcion', label: 'Descripción', required: true, placeholder: 'Descripción del servicio' },
  { name: 'alias', label: 'Alias de Facturación', placeholder: 'Alias de facturación' },
  { name: 'precio', label: 'Precio', type: 'number', placeholder: '0.00' },
];

function makeEmptyForm(): ServicioFormValues {
  return { descripcion: '', alias: '', precio: '' };
}

export interface ServicioCrearModalProps {
  open: boolean;
  proveedorId: number | null;
  companiaNombre?: string;
  /** Servicio normal (false) o especial (true). */
  especial?: boolean;
  onClose: () => void;
  onCreated?: () => void;
}

export default function ServicioCrearModal({
  open,
  proveedorId,
  companiaNombre,
  especial = false,
  onClose,
  onCreated,
}: ServicioCrearModalProps) {
  const dispatch = useAppDispatch();
  const updating = useAppSelector(s => s.categoriaServicio.updating);

  const [form, setForm] = useState<ServicioFormValues>(() => makeEmptyForm());
  const [errors, setErrors] = useState<Partial<Record<keyof ServicioFormValues, string>>>({});

  useLayoutEffect(() => {
    if (!open) return;
    setForm(makeEmptyForm());
    setErrors({});
  }, [open]);

  const handleFieldChange = (name: keyof ServicioFormValues, value: string) => {
    setForm(prev => ({ ...prev, [name]: value }));
    setErrors(prev => ({ ...prev, [name]: undefined }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (proveedorId == null) return;

    const errs: Partial<Record<keyof ServicioFormValues, string>> = {};
    if (!form.descripcion.trim()) errs.descripcion = 'La descripción es requerida';
    if (form.precio.trim() !== '' && Number.isNaN(Number(form.precio))) {
      errs.precio = 'Ingrese un precio válido';
    }
    if (Object.keys(errs).length) {
      setErrors(errs);
      return;
    }

    const precioTrim = form.precio.trim();
    const entity = {
      descripcion: form.descripcion.trim(),
      alias: form.alias.trim(),
      precio: precioTrim === '' ? null : Number(precioTrim),
      especial,
      proveedor: { id: proveedorId },
    };

    try {
      await dispatch(createEntityForProveedor({ proveedorId, entity })).unwrap();
      onCreated?.();
      onClose();
    } catch {
      /* 403 u otro error: interceptor / UI global */
    }
  };

  if (!open || proveedorId == null) return null;

  const subtitle = companiaNombre
    ? especial
      ? `Nuevo servicio especial para ${companiaNombre}.`
      : `Nuevo servicio para ${companiaNombre}.`
    : especial
      ? 'Complete los datos del nuevo servicio especial.'
      : 'Complete los datos del nuevo servicio.';

  return (
    <ProfaseModal
      open
      titleId="servicio-crear-modal-title"
      size="compact"
      zIndex={100010}
      onBackdropMouseDown={e => {
        if (e.target === e.currentTarget && !updating) onClose();
      }}
    >
      <div className="d-flex align-items-start justify-content-between gap-3 mb-2">
        <h2 id="servicio-crear-modal-title" className="profase-modal-title m-0">
          {especial ? 'Crear servicio especial' : 'Crear servicio'}
        </h2>
        <button
          type="button"
          className="profase-form-btn-secondary btn-sm flex-shrink-0"
          onClick={onClose}
          disabled={updating}
          aria-label="Cerrar"
        >
          Cerrar
        </button>
      </div>
      <p className="profase-modal-description">{subtitle}</p>
      <PageForm
        embedded
        fields={fields}
        values={form}
        errors={errors}
        isNew
        saving={updating}
        labelInline
        onFieldChange={handleFieldChange}
        onSubmit={handleSubmit}
        onCancel={onClose}
        accentColor={ACCENT}
        submitLabel={especial ? 'Crear servicio especial' : 'Crear servicio'}
        cancelLabel="Cancelar"
      />
    </ProfaseModal>
  );
}
